import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";

/** How long each panel is shown before the next one replaces it. */
const DWELL_MS = 3200;

/**
 * A backgrounded tab throttles animation frames, and using the real elapsed
 * time would advance several testimonials at once.
 */
const MAX_FRAME_MS = 100;

export default class WebQuoteController extends Controller<HTMLElement> {
  static override targets = ["tab", "panel", "mark", "row"];
  static override values = { dwell: { type: Number, default: DWELL_MS } };
  declare readonly dwellValue: number;
  declare readonly tabTargets: HTMLElement[];
  declare readonly panelTargets: HTMLElement[];
  declare readonly markTargets: HTMLElement[];
  declare readonly rowTarget: HTMLElement;
  declare readonly hasRowTarget: boolean;

  private active = 0;
  private elapsed = 0;
  private inView = false;
  private last = 0;
  private frame = 0;
  private observer: IntersectionObserver | null = null;

  override connect(): void {
    if (!this.tabTargets.length || prefersReducedMotion()) return;

    // Paused off screen, so the testimonials are not cycled through before the
    // section is in view.
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry?.isIntersecting ?? false;
      },
      { threshold: 0.35 },
    );
    this.observer.observe(this.element);
    this.frame = requestAnimationFrame((now) => this.tick(now));
  }

  override disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.last = 0;
    this.elapsed = 0;
  }

  /** Clicking a tab selects that testimonial and restarts the dwell timer. */
  select(event: Event): void {
    const index = this.tabTargets.indexOf(event.currentTarget as HTMLElement);
    if (index >= 0) this.show(index);
  }

  private show(next: number): void {
    this.active = next;
    this.elapsed = 0;
    this.tabTargets.forEach((tab, index) => {
      const on = index === next;
      if (on) tab.setAttribute("aria-current", "true");
      else tab.removeAttribute("aria-current");
      const panel = this.panelTargets[index];
      if (panel !== undefined) panel.hidden = !on;
      const mark = this.markTargets[index];
      if (mark !== undefined) mark.hidden = !on;
    });
    this.keepTabInView(next);
  }

  /**
   * The tab row scrolls horizontally on a narrow card, and the testimonial
   * advances on a timer, so the newly active tab has to be scrolled into view.
   */
  private keepTabInView(index: number): void {
    if (!this.hasRowTarget) return;
    const row = this.rowTarget;
    if (row.scrollWidth <= row.clientWidth) return;
    const tab = this.tabTargets[index];
    if (tab === undefined) return;
    row.scrollTo({
      left: Math.max(
        0,
        tab.offsetLeft - row.clientWidth / 2 + tab.clientWidth / 2,
      ),
      behavior: "smooth",
    });
  }

  /**
   * The progress bar and the advance are both computed in this one frame loop,
   * so they cannot disagree about when the switch happens.
   */
  private tick(now: number): void {
    if (this.last) {
      const delta = Math.min(now - this.last, MAX_FRAME_MS);
      if (this.inView) this.elapsed += delta;
    }
    this.last = now;
    const progress = Math.min(1, this.elapsed / this.dwellValue);
    const bar =
      this.tabTargets[this.active]?.querySelector<HTMLElement>(
        ".web-quote__bar",
      );
    if (bar) bar.style.transform = `scaleX(${progress.toFixed(4)})`;
    if (progress >= 1) this.show((this.active + 1) % this.tabTargets.length);
    this.frame = requestAnimationFrame((next) => this.tick(next));
  }
}
